import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearchOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailableOutlined';
import ExploreIcon from '@mui/icons-material/ExploreOutlined';
import { howItWorksSteps } from '../../data/landing.mock';

const iconMap = {
  search: SearchIcon,
  choose: PersonSearchIcon,
  book: EventAvailableIcon,
  explore: ExploreIcon,
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const },
  },
};

export function HowItWorksSection() {
  return (
    <Box
      component="section"
      id="how-it-works"
      sx={{
        bgcolor: 'background.paper',
        py: { xs: 8, md: 12 },
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 5, sm: 6, md: 8 } }}>
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: '#0F172A',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              mb: 1.5,
            }}
          >
            Simple Steps, Memorable Experiences
          </Typography>
          <Box
            sx={{
              width: 48,
              height: 3,
              borderRadius: 2,
              bgcolor: '#2563EB',
              mx: 'auto',
              mb: 1.5,
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '0.9375rem', md: '1.0625rem' },
              color: '#64748B',
              maxWidth: 480,
              mx: 'auto',
            }}
          >
            Find and book a verified guide in our live cities in just a few clicks.
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {howItWorksSteps.map((step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <Grid key={step.step} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box
                  component={motion.div}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ delay: index * 0.1 }}
                  sx={{
                    textAlign: 'center',
                    position: 'relative',
                  }}
                >
                  {index < howItWorksSteps.length - 1 && (
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'absolute',
                        top: 40,
                        left: '60%',
                        width: '80%',
                        borderTop: '2px dashed',
                        borderColor: 'divider',
                        zIndex: 0,
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 64, md: 80 },
                        height: { xs: 64, md: 80 },
                        borderRadius: '50%',
                        bgcolor: 'primary.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        zIndex: 1,
                        transition: 'all 300ms ease',
                        '&:hover': {
                          bgcolor: 'primary.100',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: { xs: 28, md: 32 },
                          color: 'primary.main',
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        zIndex: 2,
                      }}
                    >
                      {step.step}
                    </Box>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: '1.125rem', md: '1.125rem' },
                      fontWeight: 600,
                      color: 'text.primary',
                      mb: 1,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.875rem', md: '0.9375rem' },
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      maxWidth: { xs: 320, md: 240 },
                      mx: 'auto',
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
